import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiSearch, FiFilter, FiDownload, FiCheckCircle, FiClock, FiAlertTriangle, FiRotateCcw, FiExternalLink, FiXCircle } = FiIcons;

export default function AdminPaymentManagement() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL'); // ALL, CONFIRMED, REFUNDED, PENDING
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPayments();
  }, [filter]);

  const fetchPayments = async () => {
    setLoading(true);
    let query = supabase
      .from('app_payments_v2')
      .select(`
        *,
        profile:app_profiles(email, full_name)
      `)
      .order('created_at', { ascending: false });

    if (filter !== 'ALL') {
      query = query.eq('status', filter);
    }

    const { data, error } = await query;
    if (!error) setPayments(data);
    setLoading(false);
  };

  const filteredPayments = payments.filter(pay => {
    const term = search.toLowerCase();
    return (
      pay.profile?.full_name?.toLowerCase().includes(term) ||
      pay.profile?.email?.toLowerCase().includes(term) ||
      pay.amount.toString().includes(term) ||
      pay.asaas_payment_id.toLowerCase().includes(term)
    );
  });

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'CONFIRMED': return { label: 'Pago', style: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: FiCheckCircle };
      case 'REFUNDED': return { label: 'Estornado', style: 'bg-amber-100 text-amber-700 border-amber-200', icon: FiRotateCcw };
      case 'FAILED': return { label: 'Falhou', style: 'bg-red-100 text-red-700 border-red-200', icon: FiXCircle };
      default: return { label: 'Pendente', style: 'bg-blue-100 text-blue-700 border-blue-200', icon: FiClock };
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Extrato Asaas</h2>
          <p className="text-sm text-slate-500">Gestão financeira de pagamentos e estornos</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, email ou valor..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-indigo-500"
          >
            <option value="ALL">Todos os Status</option>
            <option value="CONFIRMED">Confirmados</option>
            <option value="REFUNDED">Estornados</option>
            <option value="PENDING">Pendentes</option>
            <option value="FAILED">Falhou</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Usuário</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Valor / Créditos</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Método</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Data/Hora</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400">Carregando transações...</td></tr>
              ) : filteredPayments.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-400">Nenhum pagamento encontrado.</td></tr>
              ) : filteredPayments.map((pay) => {
                const status = getStatusDisplay(pay.status);
                return (
                  <tr key={pay.id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-900 text-sm">{pay.profile?.full_name || 'Usuário'}</p>
                      <p className="text-[10px] text-slate-400">{pay.profile?.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-bold text-indigo-600 text-sm">R$ {pay.amount}</p>
                      <p className="text-[10px] text-slate-400">{pay.credits} créditos</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-[10px] font-black uppercase tracking-tighter text-slate-500 bg-slate-100 px-2 py-1 rounded-md">
                        {pay.billing_type === 'UNDEFINED' ? 'Asaas' : pay.billing_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {new Date(pay.created_at).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black border uppercase tracking-tighter flex items-center justify-center gap-1 mx-auto w-fit ${status.style}`}>
                        <SafeIcon icon={status.icon} />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {pay.payload?.invoiceUrl && (
                        <a 
                          href={pay.payload.invoiceUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center p-2 bg-slate-100 text-slate-400 hover:bg-indigo-600 hover:text-white rounded-xl transition-all"
                          title="Ver Fatura"
                        >
                          <SafeIcon icon={FiExternalLink} />
                        </a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}